import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  getBusinessOrders,
  getBusinessOrderById,
  updateBusinessOrderStatus,
  removeBusinessOrderItem,
  restoreBusinessOrderItem,
} from "../../api/orderService";
import { startOrderConversation } from "../../api/chatService";
import { getMyStores } from "../../api/storeService";
import OrderLabelDialog from "./OrderLabelDialog";
import {
  OrderStatusLabels,
  OrderStatusColors,
  PaymentMethodLabels,
  PaymentStatusLabels,
  PaymentStatusColors,
  DISCOUNT_TYPE_FREE_DELIVERY,
  formatCurrency,
  formatDate,
} from "../Admin/constants";

// Matches backend LockedOrderStatuses (OrdersController.cs): Delivered = 6, Cancelled = 7, Refunded = 8.
const LOCKED_ORDER_STATUSES = [6, 7, 8];

export default function Orders() {
  const { t } = useTranslation(["boOrders", "boSidebar", "boShared", "common"]);
  const ACTIVITY_TABS = [
    { value: "", label: t("boOrders:all") },
    { value: "pending", label: t("boOrders:pending") },
    { value: "delivered", label: t("boOrders:delivered") },
    { value: "cancelled", label: t("boOrders:cancelled") },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");
  const [activityGroup, setActivityGroup] = useState("");
  const [counts, setCounts] = useState({ all: 0, pending: 0, delivered: 0, cancelled: 0 });
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedOrderId, setSelectedOrderId] = useState(searchParams.get("orderId") || null);

  useEffect(() => {
    getMyStores()
      .then(setStores)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getBusinessOrders({
      storeId: storeFilter || undefined,
      activityGroup: activityGroup || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setCounts(data.counts || { all: 0, pending: 0, delivered: 0, cancelled: 0 });
        setError("");
      })
      .catch(() => setError(t("boOrders:loadError")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeFilter, activityGroup, paginationModel]);

  const handleTabChange = (event, value) => {
    setActivityGroup(value);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleRowClick = (params) => {
    setSelectedOrderId(params.row.orderId);
  };

  const handleCloseDialog = () => {
    setSelectedOrderId(null);
    if (searchParams.get("orderId")) setSearchParams({});
  };

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: t("boOrders:orderNumberColumn"), flex: 1, minWidth: 220 },
      { field: "storeName", headerName: t("boShared:storeFallback"), flex: 1, minWidth: 160 },
      {
        field: "status",
        headerName: t("boShared:status"),
        width: 160,
        renderCell: (params) => (
          <Chip size="small" label={OrderStatusLabels[params.value]} color={OrderStatusColors[params.value]} />
        ),
      },
      {
        field: "items",
        headerName: t("boOrders:columnItems"),
        width: 90,
        sortable: false,
        valueGetter: (v, row) => row.items?.length ?? 0,
      },
      {
        field: "couponCode",
        headerName: t("boShared:coupon"),
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.row.discountAmount > 0 ? (
            <Chip size="small" variant="outlined" color="success" label={params.value || t("boShared:discount")} />
          ) : null,
      },
      {
        field: "totalAmount",
        headerName: t("common:total"),
        width: 130,
        valueFormatter: (v) => formatCurrency(v),
      },
      { field: "createdAt", headerName: t("boOrders:orderedColumn"), width: 170, valueFormatter: (v) => formatDate(v) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("boSidebar:orders")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={activityGroup} onChange={handleTabChange} sx={{ mb: 2, minHeight: 36 }}>
        {ACTIVITY_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={`${tab.label} (${counts[tab.value || "all"] ?? 0})`}
            sx={{ minHeight: 36, textTransform: "none", fontWeight: 600 }}
          />
        ))}
      </Tabs>

      {stores.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t("boShared:storeFallback")}</InputLabel>
            <Select
              label={t("boShared:storeFallback")}
              value={storeFilter}
              onChange={(e) => {
                setStoreFilter(e.target.value);
                setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">{t("boShared:allStores")}</MenuItem>
              {stores.map((s) => (
                <MenuItem key={s.storeId} value={s.storeId}>
                  {s.storeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.orderId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          sx={{ border: "none", "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      <OrderDetailDialog
        orderId={selectedOrderId}
        onClose={handleCloseDialog}
        onStatusChanged={() =>
          setPaginationModel((prev) => ({ ...prev }))
        }
      />
    </Box>
  );
}

function OrderDetailDialog({ orderId, onClose, onStatusChanged }) {
  const { t } = useTranslation(["boOrders", "boShared", "common", "boOrderLabel"]);
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [showLabel, setShowLabel] = useState(false);
  const [messaging, setMessaging] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setError("");
      return;
    }
    let active = true;
    getBusinessOrderById(orderId)
      .then((data) => {
        if (active) setOrder(data);
      })
      .catch(() => {
        if (active) setError(t("boOrders:loadOrderError"));
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const open = Boolean(orderId);
  const canModifyItems = order ? !LOCKED_ORDER_STATUSES.includes(order.status) : false;
  const activeItemCount = order ? order.items.filter((i) => !i.isRemoved).length : 0;
  const hasWholesaleItem = order?.items?.some((i) => i.isWholesale);
  const netEarnings =
    order?.storeCommissionAmount != null ? order.subTotal - order.storeCommissionAmount : null;

  async function handleAdvance(status) {
    setUpdating(true);
    try {
      const updated = await updateBusinessOrderStatus(orderId, status);
      setOrder(updated);
      onStatusChanged();
    } catch {
      setError(t("boOrders:updateStatusError"));
    } finally {
      setUpdating(false);
    }
  }

  async function handleRemoveItem(item) {
    if (!window.confirm(t("boOrders:confirmRemoveItem", { name: item.productName }))) return;
    setUpdatingItemId(item.orderItemId);
    setError("");
    try {
      const updated = await removeBusinessOrderItem(orderId, item.orderItemId);
      setOrder(updated);
      onStatusChanged();
    } catch {
      setError(t("boOrders:removeItemError"));
    } finally {
      setUpdatingItemId(null);
    }
  }

  async function handleMessageCustomer() {
    if (messaging) return;
    setMessaging(true);
    setError("");
    try {
      const conversation = await startOrderConversation(orderId);
      navigate(`/business-owner/messages?conversationId=${conversation.conversationId}`);
    } catch {
      setError(t("boOrders:startConversationError"));
    } finally {
      setMessaging(false);
    }
  }

  async function handleRestoreItem(item) {
    setUpdatingItemId(item.orderItemId);
    setError("");
    try {
      const updated = await restoreBusinessOrderItem(orderId, item.orderItemId);
      setOrder(updated);
      onStatusChanged();
    } catch {
      setError(t("boOrders:restoreItemError"));
    } finally {
      setUpdatingItemId(null);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {order ? order.orderNumber : t("boOrders:orderDetails")}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {order && (
            <IconButton onClick={() => setShowLabel(true)} size="small" title={t("boOrderLabel:title")}>
              <QrCode2Icon fontSize="small" />
            </IconButton>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && !order && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {order && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">{t("boOrders:itemsCount", { count: order.items.length })}</Typography>
                    {hasWholesaleItem && <Chip size="small" label={t("boShared:wholesale")} color="secondary" variant="outlined" />}
                  </Stack>
                  <List disablePadding>
                    {order.items.map((item, idx) => {
                      const isBusy = updatingItemId === item.orderItemId;
                      const canRemoveThis = canModifyItems && !isBusy && activeItemCount > 1;
                      return (
                        <ListItem key={item.orderItemId} disableGutters divider={idx < order.items.length - 1}>
                          <ListItemAvatar>
                            <Avatar src={item.productImageUrl || undefined} variant="rounded" />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={item.isRemoved ? { textDecoration: "line-through", color: "text.disabled" } : undefined}
                              >
                                {item.productName}
                              </Typography>
                            }
                            secondary={
                              (item.variantName
                                ? `${item.variantName} • ${t("boOrders:qty", { count: item.quantity })}`
                                : t("boOrders:qty", { count: item.quantity })) +
                              (item.isRemoved ? ` • ${t("boOrders:removedOutOfStock")}` : "")
                            }
                          />
                          <Typography
                            fontWeight={600}
                            sx={item.isRemoved ? { textDecoration: "line-through", color: "text.disabled" } : undefined}
                          >
                            {formatCurrency(item.unitPrice * item.quantity)}
                          </Typography>
                          {item.isRemoved ? (
                            <Tooltip title={t("boOrders:restoreItem")}>
                              <span>
                                <IconButton size="small" disabled={!canModifyItems || isBusy} onClick={() => handleRestoreItem(item)} sx={{ ml: 1 }}>
                                  {isBusy ? <CircularProgress size={16} /> : <RestoreIcon fontSize="small" color="info" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            canModifyItems && (
                              <Tooltip title={canRemoveThis ? t("boOrders:removeItemTooltip") : t("boOrders:minItemTooltip")}>
                                <span>
                                  <IconButton size="small" disabled={!canRemoveThis} onClick={() => handleRemoveItem(item)} sx={{ ml: 1 }}>
                                    {isBusy ? <CircularProgress size={16} /> : <RemoveCircleOutlineIcon fontSize="small" color="error" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.75}>
                    <SummaryRow label={t("boOrders:subtotal")} value={formatCurrency(order.subTotal)} />
                    <SummaryRow label={t("boOrders:deliveryFee")} value={formatCurrency(order.deliveryFee)} />
                    {order.discountAmount > 0 && (
                      <SummaryRow
                        label={order.couponCode ? t("boOrders:discountWithCode", { code: order.couponCode }) : t("boShared:discount")}
                        value={`- ${formatCurrency(order.discountAmount)}`}
                      />
                    )}
                    {order.discountAmount > 0 && order.couponDiscountType === DISCOUNT_TYPE_FREE_DELIVERY && (
                      <Typography variant="caption" color="warning.main">
                        {t("boOrders:freeDeliveryCouponNote")}
                      </Typography>
                    )}
                    {order.discountAmount > 0 && order.isPlatformFundedCoupon && (
                      <Typography variant="caption" color="warning.main">
                        {t("boOrders:platformFundedNote")}
                      </Typography>
                    )}
                    <SummaryRow label={t("common:total")} value={formatCurrency(order.totalAmount)} bold />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("boOrders:statusHistory")}
                  </Typography>
                  <Stepper orientation="vertical">
                    {order.statusHistory.map((h, idx) => (
                      <Step key={idx} active completed>
                        <StepLabel>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={600}>
                              {OrderStatusLabels[h.status]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(h.changedAt)}
                            </Typography>
                          </Stack>
                          {h.note && (
                            <Typography variant="caption" color="text.secondary">
                              {h.note}
                            </Typography>
                          )}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Paper>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("boShared:status")}
                  </Typography>
                  <Chip
                    label={OrderStatusLabels[order.status]}
                    color={OrderStatusColors[order.status]}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <DetailRow label={t("boOrders:orderedColumn")} value={formatDate(order.createdAt)} />
                  {order.deliveryCompanyName && <DetailRow label={t("boOrders:deliveryBy")} value={order.deliveryCompanyName} />}
                  {order.notes && <DetailRow label={t("boOrders:notes")} value={order.notes} />}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("boOrders:payment")}
                  </Typography>
                  <Stack spacing={0.75}>
                    <DetailRow label={t("boOrders:method")} value={PaymentMethodLabels[order.paymentMethod] || "-"} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {t("boShared:status")}
                      </Typography>
                      {order.paymentStatus ? (
                        <Chip
                          size="small"
                          label={PaymentStatusLabels[order.paymentStatus]}
                          color={PaymentStatusColors[order.paymentStatus]}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          -
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>

                {order.storeCommissionAmount != null && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {t("boOrders:yourEarnings")}
                    </Typography>
                    <Stack spacing={0.75}>
                      <SummaryRow label={t("boOrders:orderSubtotal")} value={formatCurrency(order.subTotal)} />
                      <SummaryRow
                        label={
                          order.storeCommissionPercent != null
                            ? t("boOrders:platformCommissionWithPercent", { percent: order.storeCommissionPercent })
                            : t("boOrders:platformCommission")
                        }
                        value={`- ${formatCurrency(order.storeCommissionAmount)}`}
                      />
                      <SummaryRow label={t("boOrders:netEarnings")} value={formatCurrency(netEarnings)} bold />
                    </Stack>
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">{t("boOrders:customer")}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {order.customerPhone && (
                        <>
                          <Tooltip title={t("boOrders:callCustomer")}>
                            <IconButton size="small" component="a" href={`tel:${order.customerPhone}`}>
                              <CallIcon fontSize="small" color="info" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("boOrders:messageWhatsApp")}>
                            <IconButton
                              size="small"
                              component="a"
                              href={`https://wa.me/${order.customerPhone.replace(/[^\d]/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={t("boOrders:messageSalatty")}>
                        <span>
                          <IconButton size="small" disabled={messaging} onClick={handleMessageCustomer}>
                            {messaging ? <CircularProgress size={16} /> : <ChatBubbleOutlineIcon fontSize="small" color="primary" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Stack spacing={0.75}>
                    <DetailRow label={t("boOrders:name")} value={order.customerName} />
                    <DetailRow label={t("boShared:phone")} value={order.customerPhone || "-"} />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t("boOrders:deliveryAddress")}
                  </Typography>
                  <Typography variant="body2">
                    {order.addressStreet} {order.addressBuildingNumber}
                    {order.addressApartmentNumber ? t("boOrders:aptSuffix", { apt: order.addressApartmentNumber }) : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.addressCity}
                  </Typography>
                  {order.addressAdditionalDetails && (
                    <Typography variant="caption" color="text.secondary">
                      {order.addressAdditionalDetails}
                    </Typography>
                  )}
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      {order && order.availableActions?.length > 0 && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          {order.availableActions.map((status) => (
            <Button
              key={status}
              variant="contained"
              size="small"
              disabled={updating}
              onClick={() => handleAdvance(status)}
            >
              {t("boOrders:markAs", { status: OrderStatusLabels[status] })}
            </Button>
          ))}
        </DialogActions>
      )}

      {showLabel && <OrderLabelDialog order={order} onClose={() => setShowLabel(false)} />}
    </Dialog>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color={bold ? "text.primary" : "text.secondary"} fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>
        {value}
      </Typography>
    </Stack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}
